/**
 * ErrorBoundary — TRIAG-68 (2026-05-21)
 *
 * React error boundary class component. Per CONTRIBUTING
 * §"Failure visibility — silent degradation is forbidden,"
 * a white screen IS silent degradation. This component
 * catches any descendant render-time / lifecycle / setState
 * throw and renders a friendly fallback so the operator can
 * recover (Retry the boundary OR navigate Home) instead of
 * staring at a blank white viewport.
 *
 * # Why a class component?
 *
 * React 19 still requires class components for error boundaries
 * (`componentDidCatch` + `getDerivedStateFromError`). The
 * function-component equivalent (`useErrorBoundary`) lives in
 * the third-party `react-error-boundary` package, which would
 * require a new dependency per CONTRIBUTING §"Working
 * Sequentially — No Sub-agents" rule 11 (no new deps without
 * approval). The hand-rolled class is ~50 LOC and has no
 * external dependencies beyond React itself.
 *
 * # What this catches and what it doesn't
 *
 * **Catches:**
 *   - Render-time `throw new Error(...)` in any descendant component
 *   - Lifecycle method throws (componentDidMount, etc.)
 *   - Constructor throws
 *
 * **Does NOT catch (per React docs):**
 *   - Async errors in `useEffect` callbacks (those land on the
 *     window's `unhandledrejection` event)
 *   - Errors in event handlers (those bubble to `onError` which
 *     React swallows; consumers should wrap handlers in try/catch
 *     and call setState if they want to surface them)
 *   - Server-rendering errors (we're a pure SPA; not relevant)
 *   - Errors in the boundary itself (a bug in this component
 *     would surface as the original white screen)
 *
 * # Where this is mounted
 *
 * Two layers, per the audit's recommendation:
 *
 *   1. **Global** — wraps `<Routes>` in `App.tsx`. Catches throws
 *      that aren't caught by per-route boundaries (e.g. an issue
 *      in the router itself, or in `<Suspense>` fallback rendering).
 *
 *   2. **Per-route** — wraps each authenticated `<AuthGuard><Page/></AuthGuard>`
 *      element in `App.tsx`. Limits blast radius: if `/admin/llm`
 *      crashes, the operator can navigate to `/trends` and continue
 *      working. Without per-route boundaries, the global boundary's
 *      fallback would replace the WHOLE page including TopNav, so
 *      the operator couldn't navigate without a manual page reload.
 *
 * # Logging contract
 *
 * `componentDidCatch` logs the error + componentStack to
 * `console.error`. v1 deliberately does NOT POST to a server-side
 * endpoint — there's no `/api/errors` handler today and adding one
 * would balloon scope. A future enhancement could pipe to the
 * existing `useTrackPageView` infrastructure or to a new
 * `/api/errors` endpoint; the file-level docstring should be
 * updated when that lands.
 *
 * # Reset semantics
 *
 * After an error, the boundary stays in the error state until
 * either:
 *   - The user clicks the [Retry] button, which resets the boundary
 *     to render its children again. If the children still throw
 *     (e.g. a deterministic data shape that broke render), the
 *     boundary returns to the error state.
 *   - The user clicks [Go home], which navigates to `/` and unmounts
 *     the boundary entirely (a fresh boundary mounts on the home
 *     page).
 *
 * The boundary does NOT auto-reset on prop changes. The
 * `react-error-boundary` package supports a `resetKeys` prop for
 * that pattern; this hand-rolled boundary skips it because the
 * use case (programmatic reset on data refetch) doesn't fire today.
 */

import { Component, type ErrorInfo, type ReactNode } from "react";

export interface ErrorBoundaryProps {
  /** The descendants to render when no error has been caught. */
  children: ReactNode;

  /**
   * Optional fallback override. When set, this is rendered instead
   * of the default fallback UI. Receives the caught error and a
   * `reset` callback that the consumer can wire to a custom Retry
   * button.
   */
  fallback?: (error: Error, reset: () => void) => ReactNode;

  /**
   * Human-readable name for this boundary instance. Logged with the
   * error so a CloudWatch grep can identify which surface threw.
   * Defaults to "unnamed". Common values: "App", "BundlesPage",
   * "/admin/llm".
   */
  name?: string;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  /**
   * React's static API for capturing the error during rendering.
   * Returns the next state.
   */
  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  /**
   * React's instance API for side effects on error capture (logging,
   * telemetry, etc.). Runs AFTER `getDerivedStateFromError` has
   * already moved the boundary into the error state.
   */
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(
      `[ErrorBoundary:${this.props.name ?? "unnamed"}] caught error:`,
      error,
      info,
    );
  }

  /** Reset the boundary to render its children again. */
  reset = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    if (error === null) {
      return this.props.children;
    }

    if (this.props.fallback) {
      return this.props.fallback(error, this.reset);
    }

    return (
      <div
        role="alert"
        className="flex min-h-[60vh] items-center justify-center bg-gray-50 px-4 dark:bg-gray-950"
      >
        <div className="w-full max-w-md rounded-lg border border-red-200 bg-white p-6 shadow-sm dark:border-red-900 dark:bg-gray-900">
          <h1 className="text-lg font-semibold text-red-900 dark:text-red-200">
            Something went wrong
          </h1>
          <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
            This page hit an unexpected error. If it keeps happening,
            please share the error below with #triage-app-for-testing-only.
          </p>
          {/*
            Show the error message inline so an operator with the
            console open can correlate immediately, and a screenshot
            sent to Slack carries the diagnostic without requiring the
            operator to also paste console output. The message is
            constrained to one line + monospace + truncate so a long
            stack-trace-like message doesn't break the layout.
          */}
          <div className="mt-3 overflow-hidden rounded bg-gray-100 px-2 py-1.5 font-mono text-xs text-gray-700 dark:bg-gray-800 dark:text-gray-300">
            <span className="block truncate" title={error.message}>
              {error.message}
            </span>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={this.reset}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
            >
              Retry
            </button>
            <a
              href="/"
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
            >
              Go home
            </a>
          </div>
        </div>
      </div>
    );
  }
}
